import { FLOW_IN_SOCKET_ID } from "../../types";
import {
  buildDynamicSockets,
  effectiveSocketIdVersion,
  supportsSocketIdV2,
} from "./dynamicSocketId";
import type { DynamicSocketDefinition } from "./socketDefinition";

/**
 * One configured, keyed input: a list entry in a step's config (a data input,
 * a piece of evidence) that becomes one input socket.
 */
export interface KeyedInputEntry {
  /** The configured key the handler addresses the value by. */
  key: string;
  /** Everything about the socket except its id. */
  socket: Omit<DynamicSocketDefinition, "id">;
}

/** Input sockets for a step's keyed inputs, plus the socket id each key maps to. */
export interface ResolvedKeyedInputSockets {
  sockets: DynamicSocketDefinition[];
  /** Socket id for each configured key. */
  socketIdByKey: Map<string, string>;
}

/**
 * Input sockets for configured keyed inputs, shared by every step whose inputs
 * are a configured list (`entity_resolver` and `web_browser` data inputs,
 * `match_customer_with_evidence` evidence).
 *
 * The socket-id scheme comes from the step type: only a type in
 * SOCKET_ID_V2_STEP_TYPES honours a `__socketIdVersion: 2` stamp, so a handler
 * that still reads its inputs by key (web_browser) keeps bare ids while one
 * that reads through `socketIdByKey` gets marked ones. The marked fallback for
 * a key colliding with `__flow_control_in__` is gated the same way, because
 * the engine strips that id from every step's input before the handler sees
 * it, so an input of that name would otherwise arrive empty.
 */
export function resolveKeyedInputSockets(
  stepType: string,
  config: Record<string, unknown>,
  entries: KeyedInputEntry[],
): ResolvedKeyedInputSockets {
  const { sockets, assignedIds } = buildDynamicSockets({
    reserved: [FLOW_IN_SOCKET_ID],
    version: effectiveSocketIdVersion(stepType, config),
    markedFallback: supportsSocketIdV2(stepType),
    entries: entries.map((entry) => ({
      name: entry.key,
      socket: entry.socket,
    })),
  });

  const socketIdByKey = new Map<string, string>();
  entries.forEach((entry, index) => {
    const id = assignedIds[index];
    if (id !== null && id !== undefined) socketIdByKey.set(entry.key, id);
  });

  return { sockets, socketIdByKey };
}

/**
 * A handler's input values keyed by configured key: read by SOCKET ID, since
 * under `__socketIdVersion` 2 the key is not the id the value arrives on.
 * Keys with no socket, or no value on their socket, are left out.
 */
export function inputValuesByKey(
  input: Record<string, unknown>,
  socketIdByKey: ReadonlyMap<string, string>,
): Map<string, unknown> {
  const values = new Map<string, unknown>();
  for (const [key, socketId] of socketIdByKey) {
    if (socketId in input) values.set(key, input[socketId]);
  }
  return values;
}
